import { json, Link, useLoaderData } from "@remix-run/react";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { prisma } from "~/utils/db.server";
import Nav from "~/components/Nav";
import { getKindeSession } from "@kinde-oss/kinde-remix-sdk";
import { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Ticket } from "lucide-react";

export const meta: MetaFunction = () => {
  return [{ title: "Hester Theater" }];
};
export async function loader({ request }: LoaderFunctionArgs) {
  const { isAuthenticated } = await getKindeSession(request);
  const isAuthed = await isAuthenticated();

  const showtimes = await prisma.screening.findMany({
    where: {
      startTime: {
        gt: new Date(),
      },
    },
    include: {
      movie: true,
      theater: {
        include: {
          seats: true,
        },
      },
      reservedSeats: true,
      reservations: true,
    },
    orderBy: {
      startTime: "asc",
    },
  });

  return json({
    showtimes: showtimes.map((s) => ({
      id: s.id,
      movie: s.movie,
      startTime: new Date(s.startTime).toLocaleTimeString("en-US", {
        timeZone: "America/New_York",
        hour: "numeric",
        minute: "2-digit",
      }),
      date: new Date(s.startTime).toLocaleString("en-US", {
        timeZone: "America/New_York",
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      isSoldOut: s.reservedSeats.length >= s.theater.seats.length,
    })),
    isAuthenticated: isAuthed,
  });
}

export default function Showtimes() {
  const { showtimes, isAuthenticated } = useLoaderData<typeof loader>();

  return (
    <>
      <Nav isAuthenticated={isAuthenticated} />
      <div className="container mx-auto max-w-screen-xl">
        <div className="flex flex-col lg:flex-row lg:flex-wrap justify-center p-2">
          {showtimes.map((showtime) => (
            <Showing key={showtime.id} {...showtime} />
          ))}
          {showtimes.length === 0 && <NoShowtimes />}
        </div>
      </div>
    </>
  );
}

interface Movie {
  id: string;
  title: string;
  backdropUrl: string;
}

export function Showing({
  id,
  movie,
  date,
  startTime,
  isSoldOut,
}: {
  id: string;
  movie: Movie;
  date: string;
  startTime: string;
  isSoldOut: boolean;
}) {
  return (
    <div key={id} className="px-2 py-2 lg:w-1/2 box-border">
      <Card key={id} className="overflow-clip">
        <img
          src={movie.backdropUrl}
          className="inset-0 relative"
          alt={movie.title}
        />
        <div className="px-4 py-2 flex justify-between">
          <div>
            <div className="uppercase font-black">{movie.title}</div>
            <div>
              <span className="block sm:inline">{date}</span>{" "}
              <span className="hidden sm:inline">@</span> {startTime}
            </div>
          </div>
          <div className="flex items-center">
            {isSoldOut ? (
              <Button type="button" disabled>
                No Seats
              </Button>
            ) : (
              <Button asChild type="button">
                <Link to={`/screenings/${id}`}>Reserve</Link>
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export function NoShowtimes() {
  return (
    <div className="text-center p-4">
      <Ticket className="mx-auto h-12 w-12" />
      <h3 className="mt-2">No scheduled showtimes. Please check back later.</h3>
    </div>
  );
}
